import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch } from 'react-redux'
import { toast } from 'react-toastify'

import { useAppSelector } from '../store'
import {
  useCalibrateImageMutation,
  useGetCaseDetailsQuery,
} from '../store/apiService'
import {
  ImageCalibrationStatus,
  setGalleryImageLodaingMap,
  setImageCalibrationStatus,
} from '../store/sessionSlice'
import { getCaseId } from '../utils'
import { GENERAL_ERROR } from '../utils/constants'

interface CaseImage {
  id: string | number
  image: string
  calibrated_image: string
  type: string
  url: string
  width: number
  height: number
}

interface UseCalibrationProps {
  canvasImage: {
    id: string | number
    type: string
  } | null
  value: string
  onCalibrationComplete?: (calibratedImage: CaseImage) => void
  imageId: any
}

export const useCalibration = ({
  imageId,
  canvasImage,
  value,
  onCalibrationComplete,
}: UseCalibrationProps) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'calibration',
  })
  const dispatch = useDispatch()
  const caseId = getCaseId()
  const [calibrate] = useCalibrateImageMutation()
  const [pollingInterval, setPollingInterval] = useState(0)
  const [isCalibrated, setIsCalibrated] = useState(false)

  const imageCalibrationStatus = useAppSelector((state) => {
    const status = state.session.imageCalibrationStatus[imageId || '']
    return status ?? ImageCalibrationStatus.CALIBRATE
  })
  const imagePixelInfo = useAppSelector((state) => state.app.imagePixelInfo)
  const newImages = useAppSelector((state) => state.app.newImages)

  const { data: caseDetails, refetch } = useGetCaseDetailsQuery(
    { caseId: caseId },
    {
      skip: !caseId,
      pollingInterval,
      skipPollingIfUnfocused: true,
    },
  )

  // Effect to handle polling cleanup
  useEffect(() => {
    return () => {
      if (pollingInterval) {
        setPollingInterval(0)
      }
    }
  }, [pollingInterval])

  // Effect to check calibration status
  useEffect(() => {
    if (caseDetails?.images) {
      const calibratedImage = caseDetails.images.find(
        (image) => image.id === canvasImage?.id && !!image.calibrated_image,
      )
      if (calibratedImage && isCalibrated) {
        dispatch(setGalleryImageLodaingMap({ [canvasImage?.id || '']: false }))
        setPollingInterval(0)
        toast.success(t('calibrationComplete'))
        setIsCalibrated(false)
        onCalibrationComplete?.(calibratedImage)
      }
    }
  }, [
    caseDetails,
    canvasImage?.id,
    dispatch,
    onCalibrationComplete,
    isCalibrated,
  ])

  // Effect to handle calibration process
  useEffect(() => {
    if (!canvasImage?.id || !value || !newImages[0]?.image) {
      return
    }

    if (ImageCalibrationStatus.CALIBRATED === imageCalibrationStatus) {
      dispatch(setGalleryImageLodaingMap({ [canvasImage?.id || '']: true }))

      const startCalibration = async () => {
        try {
          await calibrate({
            id: canvasImage?.id || newImages[0]?.id,
            L: value,
            x: imagePixelInfo?.x,
            y: imagePixelInfo?.y,
          }).unwrap()

          setPollingInterval(3000)
          toast.info(t('inProgress'))

          await refetch()
        } catch (e: any) {
          toast.error(e?.data?.message || GENERAL_ERROR)
          setPollingInterval(0)
          dispatch(setGalleryImageLodaingMap({}))
        } finally {
          dispatch(
            setImageCalibrationStatus({
              id: canvasImage?.id,
              status: ImageCalibrationStatus.CALIBRATE,
            }),
          )
          setIsCalibrated(true)
        }
      }

      startCalibration()
    }
  }, [
    imageCalibrationStatus,
    canvasImage?.id,
    value,
    imagePixelInfo,
    calibrate,
    dispatch,
    refetch,
  ])

  const handleCalibrationClick = () => {
    const status =
      imageCalibrationStatus === ImageCalibrationStatus.CALIBRATE
        ? ImageCalibrationStatus.CALIBRATING
        : ImageCalibrationStatus.CALIBRATE
    dispatch(setImageCalibrationStatus({ id: canvasImage?.id, status: status }))
  }

  const resetCalibration = () => {
    dispatch(
      setImageCalibrationStatus({
        id: canvasImage?.id,
        status: ImageCalibrationStatus.CALIBRATE,
      }),
    )
    setIsCalibrated(false)
  }

  const buttonLabel =
    imageCalibrationStatus === ImageCalibrationStatus.CALIBRATING
      ? t('pickPoint')
      : t('Calibrate')
  return {
    buttonLabel,
    handleCalibrationClick,
    resetCalibration,
    isCalibrating:
      imageCalibrationStatus === ImageCalibrationStatus.CALIBRATING,
    isDisabled: !value,
  }
}
